import React, { useEffect, useRef, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

interface Notification {
  id: string;
  type: 'like' | 'comment' | 'reaction';
  postId: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  read: boolean;
  createdAt: any;
  reactionType?: string;
  reactionEmoji?: string;
  content?: string; // For comment content
  postContent?: string; // Preview of the post content
}

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement>;
  onSelectPost: (postId: string, commentId: string | null) => void;  
}


export default function NotificationsModal({ isOpen, onClose, anchorRef, onSelectPost }: NotificationsModalProps) {
  const { user } = useAuth();
  const { notifications, error } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [displayCount, setDisplayCount] = useState(5);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

  // Mark notifications as read when opened
  useEffect(() => {
    if (!user || !isOpen) return;

    const markNotificationsAsRead = async () => {
      try {
        const unreadNotifications = notifications.filter(n => !n.read);
        const updatePromises = unreadNotifications.map(notification => 
          updateDoc(doc(db, 'notifications', notification.id), {
            read: true
          })
        );
        await Promise.all(updatePromises);
      } catch (err) {
        console.error('Error marking notifications as read:', err);
      }
    };

    // Wait a bit before marking as read to ensure the animation is smooth
    const timer = setTimeout(markNotificationsAsRead, 1000);
    return () => clearTimeout(timer);
  }, [isOpen, notifications, user]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && 
          !panelRef.current.contains(event.target as Node) && 
          anchorRef.current && 
          !anchorRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleNotificationClick = (notification: Notification, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!notification.postId) {
      console.error('No postId found in notification:', notification);
      return;
    }

    console.log('Handling notification click:', notification);
    onClose();
    
    // Only pass commentId for comment-related notifications
    const commentId = notification.type === 'comment' ? notification.content : null;
    onSelectPost(notification.postId, commentId);
  };


  if (!isOpen) return null;

  // Calculate position based on anchor element
  const anchorRect = anchorRef.current?.getBoundingClientRect();
  const top = (anchorRect?.bottom ?? 0) + window.scrollY;
  const right = window.innerWidth - (anchorRect?.right ?? 0);

  return (
    <div
      ref={panelRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[100] overflow-hidden"
      style={{
        top: isMobile ? '80px' : `${top}px`,
        right: isMobile ? '50%' : `${right}px`,
        transform: isMobile ? 'translateX(50%)' : 'none',
        width: isMobile ? '95%' : 'auto',
        maxWidth: '350px',
        minWidth: isMobile ? 'auto' : '300px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '70vh'
      }}
    >
      <div className="p-3 border-b border-gray-200 bg-orange-500">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Bell size={18} />
          Notifications
        </h3>
      </div>

      <div className="overflow-y-auto max-h-[calc(70vh-4rem)]">
        {error ? (
          <div className="p-6 text-center">
            <p className="text-red-500 mb-2">{error}</p>
            <p className="text-gray-500 text-sm">Please try again later or contact support if the problem persists.</p>
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No notifications yet</p>
        ) : (
          <>
            <div className="divide-y divide-gray-100">
              {notifications.slice(0, displayCount).map((notification) => (
                <button
                  key={notification.id}
                  className="w-full text-left flex items-start gap-3 p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={(e) => handleNotificationClick(notification, e)}
                >
                  <div className="flex-1">
                    <p className="text-gray-900 text-sm">
                      <span className="font-semibold">{notification.fromUserName}</span>{' '}
                      {notification.type === 'like' && 'liked '}
                      {notification.type === 'comment' && 'commented on '}
                      {notification.type === 'reaction' && (
                        <>reacted with {notification.reactionEmoji} to </>
                      )}
                      your post
                    </p>
                    {notification.type === 'comment' && notification.content && (
                      <p className="mt-1 text-gray-600 text-xs line-clamp-2">
                        "{notification.content}"
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDistanceToNow(notification.createdAt?.toDate(), { addSuffix: true })}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-1" />
                  )}
                </button>
              ))}
            </div>
            {notifications.length > displayCount && (
              <button
                onClick={() => setDisplayCount(prev => prev + 5)}
                className="w-full py-2 text-sm text-orange-600 hover:bg-orange-50 transition-colors border-t border-gray-100"
              >
                Load More
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
